import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppService } from '../../../services/app.service';
import { ToastrService } from 'ngx-toastr';
import { EnquiryRequest } from '../../../models/enquiry.model';
import { Subject, forkJoin, takeUntil } from 'rxjs';

@Component({
    selector: 'app-products',
    templateUrl: './products.component.html',
    styleUrl: './products.component.scss'
})
export class ProductsComponent implements OnInit, OnDestroy {
    destroy$ = new Subject<void>();
    selectedCategory = 0; // 0 = All
    searchQuery = '';

    categories: any[] = [];
    products: any[] = [];
    filteredProducts: any[] = [];
    isLoading = true;

    // Enquiry modal
    isEnquiryModalOpen = false;
    enquiryProductId: number = 0;
    enquiryProductName: string = '';
    enquiryForm: EnquiryRequest = this.getEmptyEnquiry();
    isSubmitting = false;

    constructor(
        private route: ActivatedRoute,
        private appService: AppService,
        private toaster: ToastrService
    ) { }

    ngOnInit(): void {
        this.loadData();
    }

    loadData(): void {
        this.isLoading = true;
        forkJoin({
            products: this.appService.getAvailableProducts(),
            categories: this.appService.getAllCategories()
        }).pipe(takeUntil(this.destroy$)).subscribe({
            next: (data) => {
                this.products = data.products || [];
                this.categories = data.categories || [];
                this.isLoading = false;

                // Check for category query param
                this.route.queryParams.subscribe(params => {
                    if (params['category']) {
                        this.selectedCategory = +params['category'];
                    }
                    this.filterProducts();
                });
            },
            error: (err) => {
                console.error('Error loading data', err);
                this.isLoading = false;
            }
        });
    }

    selectCategory(categoryId: number) {
        this.selectedCategory = categoryId;
        this.filterProducts();
    }

    filterProducts() {
        let products = this.products;
        if (this.selectedCategory !== 0) {
            products = products.filter(p => p.categoryId === this.selectedCategory);
        }
        if (this.searchQuery.trim()) {
            const query = this.searchQuery.toLowerCase();
            products = products.filter(p =>
                (p.productName || '').toLowerCase().includes(query) ||
                (p.description || '').toLowerCase().includes(query)
            );
        }
        this.filteredProducts = products;
    }

    onSearch() {
        this.filterProducts();
    }

    getCategoryName(categoryId: number): string {
        const cat = this.categories.find(c => c.categoryId === categoryId);
        return cat ? cat.categoryName : '';
    }

    getProductCount(categoryId: number): number {
        if (categoryId === 0) return this.products.length;
        return this.products.filter(p => p.categoryId === categoryId).length;
    }

    getProductImage(product: any): string {
        if (product.imageDetails && product.imageDetails.length > 0) {
            const primaryImg = product.imageDetails.find((img: any) => img.primary) || product.imageDetails[0];
            return this.appService.getProductImageUrl(primaryImg.imageDetailsId);
        }
        return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' fill='%23ccc'%3E%3Crect width='400' height='300' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='45%25' text-anchor='middle' font-size='48' fill='%23ccc'%3E%F0%9F%93%B7%3C/text%3E%3Ctext x='50%25' y='60%25' text-anchor='middle' font-size='16' fill='%23aaa' font-family='sans-serif'%3ENo Image%3C/text%3E%3C/svg%3E`;
    }

    // --- Enquiry Modal ---
    getEmptyEnquiry(): EnquiryRequest {
        return {
            productId: 0,
            customerName: '',
            phoneNumber: '',
            doorNo: '',
            village: '',
            street: '',
            city: '',
            state: '',
            pincode: '',
            message: ''
        };
    }

    openEnquiryModal(product: any): void {
        this.enquiryProductId = product.productId;
        this.enquiryProductName = product.productName;
        this.enquiryForm = this.getEmptyEnquiry();
        this.enquiryForm.productId = product.productId;
        this.isEnquiryModalOpen = true;
    }

    closeEnquiryModal(): void {
        this.isEnquiryModalOpen = false;
    }

    submitEnquiry(): void {
        if (!this.enquiryForm.customerName || !this.enquiryForm.phoneNumber) {
            this.toaster.warning('Please fill in your name and phone number.');
            return;
        }
        this.isSubmitting = true;
        this.appService.sendEnquiry(this.enquiryForm).subscribe({
            next: () => {
                this.isSubmitting = false;
                this.toaster.success('Enquiry sent successfully! We will contact you soon.');
                this.closeEnquiryModal();
            },
            error: (err) => {
                console.error('Error sending enquiry', err);
                this.isSubmitting = false;
                this.toaster.error('Failed to send enquiry. Please try again.');
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
